import { Toaster } from "@vaiu/ui/toaster";
import { useToast } from "@/hooks/use-toast";
import { Toaster as Sonner } from "@vaiu/ui/sonner";
import { TooltipProvider } from "@vaiu/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthProvider, AuthNavigationSync, useAuth } from "@/context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { BiometricProvider } from "./context/BiometricContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import { ConfigProvider, useConfig } from "./context/ConfigContext";
import { CashTokenCardProvider } from "@vaiu/cash-token-card";
import { boPaymentsApi, platformApi } from "@/services/platformApi";
import PrepaidCard from "@/components/payment/PrepaidCard";
import NFCPayment from "@/components/NFCPayment";
import { InvoicePaymentDecision } from "@vaiu/redeem-ux";
import { VaiuRedeemUxProvider } from "@/components/redeem/VaiuRedeemUxProvider";
import { BrandThemeProvider } from "./context/BrandThemeContext";
import Footer from "./components/shared/Footer";
import ScrollToTop from "./components/ScrollToTop";
import "./styles/tour.css";
import Index from "./pages/Index";
import LoginPage from "./pages/LoginPage";
import Signup from "./pages/Signup";
import SignupWithType from "./pages/SignupWithType";
import MerchantToolsRedirect from "./components/redirects/MerchantToolsRedirect";
import MerchantOnboardingRedirect from "./components/redirects/MerchantOnboardingRedirect";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ResetPasswordWithTokenPage } from "./pages/ResetPasswordWithTokenPage";
import { ResetPasswordRedirect } from "./pages/ResetPasswordRedirect";
import Contacts from "./pages/Contacts";
import ContactDetailPage from "./pages/ContactDetailPage";
import BusinessDetail from "./pages/BusinessDetail";
import NotFound from "./pages/NotFound";
import ProfilePage from "./pages/ProfilePage";
import InboxPage from "./pages/InboxPage";
import PaymentSourcesPage from "./pages/PaymentSourcesPage";
import AddPaymentMethodPage from "./pages/AddPaymentMethodPage";
import TransferOutPage from "./pages/TransferOutPage";
import TopupPage from "./pages/TopupPage";
import TransactionHistoryPage from "./pages/TransactionHistoryPage";
import ConversationsPage from "./pages/ConversationsPage";
import MessagesPage from "./pages/MessagesPage";
// Merchant pages moved to sell.vaiu.app - redirecting instead
// import MerchantProfilePage from "./pages/MerchantProfilePage";
// import MerchantToolsPage from "./pages/MerchantToolsPage";
import MerchantProfileRedirect from "./components/redirects/MerchantProfileRedirect";
import MerchantPublicProfile from "./pages/MerchantPublicProfile";
import UserPublicProfile from "./pages/UserPublicProfile";
import MerchantDirectory from "./pages/MerchantDirectory";
import CurrencyMarketPage from "./pages/CurrencyMarketPage";
import StockExchangePage from "./pages/StockExchangePage";
import PortfolioPage from "./pages/PortfolioPage";
import UnifiedMarketplace from "./pages/marketplace/UnifiedMarketplace";
import SmartTransactionsPage from "./pages/SmartTransactionsPage";
import AppShell from "./components/AppShell";
import ConditionalAppShell from "./components/ConditionalAppShell";
import EmbedRouteGuard from "./components/EmbedRouteGuard";
import EthereumFundingPage from "./pages/EthereumFundingPage";
import PayPage from "./pages/PayPage";
import EmbeddedTestPage from "./pages/EmbeddedTestPage";
import PosCheckoutPage from "./pages/PosCheckoutPage";
import InvoicePaymentPage from "./pages/InvoicePaymentPage";
import RedeemPage from "./pages/RedeemPage";
import ReferralTokensPage from "./pages/ReferralTokensPage";
import ReferralsRoute from "./components/ReferralsRoute";
import HelpPage from "./pages/HelpPage";
import SupportPage from "./pages/SupportPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import CookieSettingsPage from "./pages/CookieSettingsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ExchangePage from "./pages/ExchangePage";
import IntellectDesignTestPage from "./pages/IntellectDesignTestPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import AcknowledgementsPage from "./pages/AcknowledgementsPage";
import UIAPIPage from "./pages/UIAPIPage";
import { VaiuShell } from "@vaiu/ui";
import { createVaiuAppShellExecutor } from "@/shell/createVaiuAppShellExecutor";
import OnboardingFlow from "./components/OnboardingFlow";
import MerchantRequestsPage from "./pages/MerchantRequestsPage";
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import ProtectedAdminRoute from "./components/admin/ProtectedAdminRoute";
import { ProtectedRoute } from "./components/auth";
import LabsRoute from "./components/LabsRoute";
import IntellectRoute from "./components/IntellectRoute";
import { disableScrollRestoration } from "./utils/scrollUtils";
import { analyticsService } from "./services/analyticsService";
import { getAppTitle } from "./utils/domainDetection";
import { ErrorBoundary } from "@vaiu/ui";

// Developer pages - MOVED TO vaiu-developer-app (developers.vaiu.io)
// import DeveloperConsole from "./pages/developer/DeveloperConsole";
// import DeveloperTermsPage from "./pages/developer/DeveloperTermsPage";
// import NotificationTemplates from "./pages/developer/NotificationTemplates";
// import TemplateEditor from "./pages/developer/TemplateEditor";
import BrandThemeDemo from "./pages/BrandThemeDemo";
import BrandTestPage from "./pages/BrandTestPage";
import InvoiceBreakdownDemo from "./pages/InvoiceBreakdownDemo";
import CorePageContent from "./components/CorePageContent";

const StorefrontToMerchantRedirect: React.FC = () => {
  const { merchantId } = useParams<{ merchantId: string }>();
  if (!merchantId) {
    return <Navigate to="/marketplace" replace />;
  }
  return <Navigate to={`/merchant/${encodeURIComponent(merchantId)}`} replace />;
};

/** Legacy inbox/deep links used /tokens/:code before /transactions/:code existed. */
const TokenTransactionsRedirect: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  if (!code) {
    return <Navigate to="/transactions" replace />;
  }
  return <Navigate to={`/transactions/${encodeURIComponent(code)}`} replace />;
};

// Developer Console redirect - redirects to external developers.vaiu.io
const DeveloperRedirect: React.FC = () => {
  useEffect(() => {
    window.location.href = 'https://developers.vaiu.io';
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <p className="text-gray-600">Redirecting to Developer Console...</p>
      </div>
    </div>
  );
};

// Wrapper component to provide dependencies to VaiuShell
const VaiuShellWrapper = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const shellExecutor = useMemo(
    () =>
      createVaiuAppShellExecutor(
        platformApi,
        () => user?.currency ?? 'USD'
      ),
    [user?.currency]
  );
  return <VaiuShell shellExecutor={shellExecutor} toast={toast} />;
};

// PayPal redirect handler component
const PayPalRedirect: React.FC<{ status: 'complete' | 'cancelled' }> = ({ status }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleRedirect = () => {
      const searchParams = new URLSearchParams(location.search);
      const paymentId = searchParams.get('paymentId');
      let context = searchParams.get('context');
      
      // Check if this is a token creation context
      let returnPath = '/topup'; // Default return path
      let contextParams = '';
      
      // If context is not in URL, try to get it from sessionStorage
      if (!context) {
        const pendingTokenData = sessionStorage.getItem('pendingTokenCreation');
        if (pendingTokenData) {
          try {
            const parsed = JSON.parse(pendingTokenData);
            // Reconstruct context from stored data
            const tokenParams = new URLSearchParams({
              tokenAmount: parsed.amount?.toString() || '',
              tokenCurrency: parsed.currency || '',
              ...(parsed.message && { tokenMessage: parsed.message })
            });
            context = `create-token:${tokenParams.toString()}`;
          } catch (e) {
            console.error('Failed to reconstruct context from sessionStorage:', e);
          }
        }
      }
      
      // Handle both old 'create-token:' and new 'topup:' contexts
      if (context && (context.startsWith('create-token:') || context.startsWith('topup:'))) {
        // Extract token creation data from context (for old format)
        if (context.startsWith('create-token:')) {
          const tokenDataStr = context.replace('create-token:', '');
          const tokenParams = new URLSearchParams(tokenDataStr);
        }
        
        // Try to get return path from localStorage first (stored by CreateCashToken)
        // Then check sessionStorage, then fallback to home page
        const pendingTokenData = localStorage.getItem('pendingTokenCreation');
        if (pendingTokenData) {
          try {
            const parsed = JSON.parse(pendingTokenData);
            // Check if there's a returnUrl stored (new format) or returnPath (old format)
            if (parsed.returnUrl) {
              // New format: returnUrl is a full URL, extract pathname
              try {
                const returnUrlObj = new URL(parsed.returnUrl);
                returnPath = returnUrlObj.pathname + returnUrlObj.search;
              } catch (e) {
                // If parsing fails, use as-is (might already be a path)
                returnPath = parsed.returnUrl;
              }
            } else if (parsed.returnPath) {
              // Old format: returnPath is a path
              returnPath = parsed.returnPath;
            } else {
              // Fallback: check session storage for the original path
              const originalPath = sessionStorage.getItem('paypalOriginalPath');
              if (originalPath) {
                returnPath = originalPath;
              } else {
                // Default to home page instead of hard-coded /pay
                returnPath = '/';
              }
            }
          } catch (e) {
            console.error('Failed to parse pending token data:', e);
            // Fallback to session storage or home page
            const originalPath = sessionStorage.getItem('paypalOriginalPath');
            returnPath = originalPath || '/';
          }
        } else {
          // No localStorage, check sessionStorage for original path or default to home page
          const originalPath = sessionStorage.getItem('paypalOriginalPath');
          returnPath = originalPath || '/';
        }
        
        // Preserve context for CreateCashToken component
        contextParams = `&context=${encodeURIComponent(context)}`;
      } else {
        // Not a token creation context, check for original path
        const originalPath = sessionStorage.getItem('paypalOriginalPath');
        if (originalPath) {
          returnPath = originalPath;
        } else {
          // Default to home page instead of hard-coded /pay
          returnPath = '/';
        }
      }
      
      // Preserve other query parameters (excluding paymentId and context which we handle separately)
      const otherParams = new URLSearchParams();
      searchParams.forEach((value, key) => {
        if (key !== 'paymentId' && key !== 'context' && key !== 'status') {
          otherParams.append(key, value);
        }
      });
      const otherQueryString = otherParams.toString() ? `&${otherParams.toString()}` : '';
      
      sessionStorage.removeItem('paypalOriginalPath');
      const paypalFlag = status === 'cancelled' ? 'cancelled' : 'complete';
      const paymentQuery = paymentId ? `&paymentId=${encodeURIComponent(paymentId)}` : '';
      navigate(`${returnPath}?paypal=${paypalFlag}${paymentQuery}${contextParams}${otherQueryString}`, { replace: true });
    };

    handleRedirect();
  }, [status, location.search, navigate]);

  // Default: redirecting (should be very brief)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Redirecting...</p>
      </div>
    </div>
  );
};

// Lazy load admin components for performance optimization
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminAnalytics = React.lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminTransactions = React.lazy(() => import('./pages/admin/AdminTransactions'));
const AdminReferrals = React.lazy(() => import('./pages/admin/AdminReferrals'));
const AdminSupport = React.lazy(() => import('./pages/admin/AdminSupport'));
const AdminGuestConversations = React.lazy(() => import('./pages/admin/AdminGuestConversations'));
const UserVotes = React.lazy(() => import('./pages/admin/UserVotes'));
const AIAgentManagement = React.lazy(() => import('./pages/admin/AIAgentManagement'));
const AdminClients = React.lazy(() => import('./pages/admin/AdminClients'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminMerchantOnboarding = React.lazy(() => import('./pages/admin/AdminMerchantOnboardingFixed'));
const AdminMerchantInsights = React.lazy(() => import('./pages/admin/AdminMerchantInsights'));
const SystemAdmin = React.lazy(() => import('./pages/admin/SystemAdmin'));

// Loading component for admin pages
const AdminLoadingFallback = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-gray-600">Loading admin panel...</p>
    </div>
  </div>
);

const queryClient = new QueryClient();

const App = () => {
  // Track language in state to force re-render when language changes
  const [languageKey, setLanguageKey] = useState(0);

  // Disable browser's automatic scroll restoration since we handle it manually
  useEffect(() => {
    disableScrollRestoration();
    
    // Initialize Google Analytics
    analyticsService.initialize().catch(error => {
      console.error('Failed to initialize analytics:', error);
    });

    // Set document title based on domain
    const appTitle = getAppTitle();
    document.title = appTitle;

    // Listen for language changes and force re-render of entire app
    const handleLanguageChange = () => {
      setLanguageKey(prev => prev + 1);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster useToast={useToast} />
          <Sonner />
          <AuthProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AuthNavigationSync />
              <ScrollToTop />
              <NotificationProvider>
                <BiometricProvider>
                  <CookieConsentProvider>
                    <ConfigProvider>
                      <CashTokenCardProvider
                        useToast={useToast}
                        useAuth={useAuth}
                        useConfig={useConfig}
                        boPaymentsApi={boPaymentsApi}
                        updateTokenConditions={(tokenKey, body) =>
                          platformApi.tokens.updateTokenConditions(tokenKey, body)
                        }
                        components={{
                          PrepaidCard,
                          NFCPayment,
                          InvoicePaymentDecision,
                        }}
                      >
                      <VaiuRedeemUxProvider>
                      <BrandThemeProvider>
                        <div key={languageKey} className="min-h-screen flex flex-col">
                          <main className="flex-1">
                            <EmbedRouteGuard>
                            <Routes>
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/signin" element={<LoginPage />} />
                            <Route path="/signup" element={<SignupWithType />} />
                            <Route path="/signup-old" element={<Signup />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/reset-password/:token" element={<ResetPasswordWithTokenPage />} />
                            {/* Catch-all routes for various reset password URL formats */}
                            <Route path="/reset/:token" element={<ResetPasswordRedirect />} />
                            <Route path="/reset-password" element={<ResetPasswordRedirect />} />
                            <Route path="/reset" element={<ResetPasswordRedirect />} />
                            <Route path="/register" element={<Signup />} />
                            <Route path="/" element={<Index />} />
                            <Route path="/onboarding" element={<OnboardingFlow />} />
                            <Route path="/merchant-requests" element={<MerchantRequestsPage />} />
                            {/* Redirect /dashboard to / for backward compatibility */}
                            <Route path="/dashboard" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <CorePageContent />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/profile" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <ProfilePage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/contacts" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <Contacts />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/contacts/:contactId" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <ContactDetailPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/inbox" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <InboxPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/payment-sources" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <PaymentSourcesPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/payment-methods/add" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <AddPaymentMethodPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/transfer" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <TransferOutPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/transfer-out/:currency" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <TransferOutPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/topup" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <TopupPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/funding/ethereum" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <EthereumFundingPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/funding/success" element={
                              <PayPalRedirect status="complete" />
                            } />
                            <Route path="/funding/cancel" element={
                              <PayPalRedirect status="cancelled" />
                            } />
                            <Route path="/tokens/:code" element={<TokenTransactionsRedirect />} />
                            <Route path="/transactions/detail/:code" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <TransactionHistoryPage autoOpenDetails />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/transactions/:code" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <TransactionHistoryPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/transactions" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <TransactionHistoryPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/messages" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <MessagesPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/conversations" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <MessagesPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                                                        <Route path="/profile/merchant" element={
                                <ProtectedRoute>
                                    <MerchantProfileRedirect />
                                </ProtectedRoute>
                            } />
                            <Route path="/merchant-onboarding" element={
                                <ProtectedRoute>
                                    <MerchantOnboardingRedirect path="/merchant-onboarding" />
                                </ProtectedRoute>
                            } />
                            <Route path="/merchant/onboarding-choice" element={
                                <ProtectedRoute>
                                    <MerchantOnboardingRedirect path="/merchant/onboarding-choice" />
                                </ProtectedRoute>
                            } />
                            <Route path="/merchant/onboarding/manual" element={
                                <ProtectedRoute>
                                    <MerchantOnboardingRedirect path="/profile/merchant" />
                                </ProtectedRoute>
                            } />
                            <Route path="/merchant-tools" element={
                              <ProtectedRoute requiredRoles={['MERCHANT']}>
                                <MerchantToolsRedirect />
                              </ProtectedRoute>
                            } />
                            <Route path="/merchant/fast-track" element={
                              <ProtectedRoute>
                                <MerchantOnboardingRedirect path="/merchant/fast-track" />
                              </ProtectedRoute>
                            } />
                            <Route path="/merchant/preview" element={
                              <ProtectedRoute>
                                <MerchantOnboardingRedirect path="/merchant/preview" />
                              </ProtectedRoute>
                            } />
                            <Route path="/merchant/welcome" element={
                              <ProtectedRoute>
                                <MerchantOnboardingRedirect path="/merchant/welcome" />
                              </ProtectedRoute>
                            } />
                            {/* Host page: vaiu.app in a sized iframe for embed UX testing */}
                            <Route path="/embedded" element={<EmbeddedTestPage />} />
                            {/* PROTECTED ROUTE - /pay allows unauthenticated access with guest choice UI */}
                            <Route path="/pay/:merchantId?" element={
                              <ProtectedRoute allowUnauthenticated={true}>
                                <ConditionalAppShell>
                                  <PayPage />
                                </ConditionalAppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/pos" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <PosCheckoutPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            {/* Public merchant profile routes */}
                            <Route path="/merchant/:merchantId" element={
                              <ProtectedRoute allowGuests={true}>
                                <ConditionalAppShell>
                                  <MerchantPublicProfile />
                                </ConditionalAppShell>
                              </ProtectedRoute>
                            } />
                            {/* Public user profile routes */}
                            <Route path="/user/:userId" element={
                              <ProtectedRoute>
                                <ConditionalAppShell>
                                  <UserPublicProfile />
                                </ConditionalAppShell>
                              </ProtectedRoute>
                            } />
          <Route path="/merchants" element={<Navigate to="/marketplace" replace />} />
          <Route path="/merchant-directory" element={<Navigate to="/marketplace" replace />} />
          <Route path="/marketplace" element={
            <AppShell>
              <UnifiedMarketplace />
            </AppShell>
          } />
          <Route path="/portfolio" element={
            <ProtectedRoute>
              <LabsRoute>
              <AppShell>
                <PortfolioPage />
              </AppShell>
              </LabsRoute>
            </ProtectedRoute>
          } />
                            {/* Legacy storefront route - can be removed later */}
                            <Route path="/storefront/:merchantId" element={<StorefrontToMerchantRedirect />} />
                            <Route path="/invoice/:invoiceCode" element={
                              <ProtectedRoute>
                                <InvoicePaymentPage />
                              </ProtectedRoute>
                            } />
                            <Route path="/business/:id" element={<BusinessDetail />} />
                            <Route path="/redeem" element={
                              <ProtectedRoute>
                                <RedeemPage />
                              </ProtectedRoute>
                            } />
                            <Route path="/redeem/:code" element={
                              <ProtectedRoute>
                                <RedeemPage />
                              </ProtectedRoute>
                            } />
                            <Route path="/referral-tokens" element={
                              <ProtectedRoute>
                                <ReferralsRoute>
                                <AppShell>
                                  <ReferralTokensPage />
                                </AppShell>
                                </ReferralsRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/help" element={
                              <AppShell>
                                <HelpPage />
                              </AppShell>
                            } />
                            <Route path="/support" element={
                              <ProtectedRoute allowGuests={true}>
                                <AppShell>
                                  <SupportPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                            <Route path="/cookie-settings" element={<CookieSettingsPage />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                            <Route path="/acknowledgements" element={<AcknowledgementsPage />} />
                            <Route path="/exchange" element={
                              <ProtectedRoute>
                                <LabsRoute>
                                <AppShell>
                                  <ExchangePage />
                                </AppShell>
                                </LabsRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/currency-market" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <CurrencyMarketPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/smart-transactions" element={
                              <ProtectedRoute>
                                <LabsRoute>
                                <AppShell>
                                  <SmartTransactionsPage />
                                </AppShell>
                                </LabsRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/stocks" element={
                              <ProtectedRoute>
                                <LabsRoute>
                                <AppShell>
                                  <StockExchangePage />
                                </AppShell>
                                </LabsRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/stock-exchange" element={
                              <ProtectedRoute>
                                <Navigate to="/stocks" replace />
                              </ProtectedRoute>
                            } />
                            <Route path="/memoney" element={
                              <ProtectedRoute>
                                <Navigate to="/profile#memoney" replace />
                              </ProtectedRoute>
                            } />
                            <Route path="/intellect-test" element={
                              <ProtectedRoute>
                                <IntellectRoute>
                                  <AppShell>
                                    <IntellectDesignTestPage />
                                  </AppShell>
                                </IntellectRoute>
                              </ProtectedRoute>
                            } />
                            <Route path="/invoice-breakdown-demo" element={<InvoiceBreakdownDemo />} />
                            <Route path="/shell" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <VaiuShellWrapper />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/uiapi" element={<UIAPIPage />} />
                            {/* Developer routes - MOVED TO vaiu-developer-app (developers.vaiu.io) */}
                            {/* Redirect /developer to external developers.vaiu.io */}
                            <Route path="/developer" element={<DeveloperRedirect />} />
                            <Route path="/developer/*" element={<DeveloperRedirect />} />
                            {/* 
                            <Route path="/developer" element={
                              <ProtectedRoute requiredRoles={['DEVELOPER', 'ADMIN']}>
                                <AppShell>
                                  <DeveloperConsole />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/developer/console" element={
                              <ProtectedRoute requiredRoles={['DEVELOPER', 'ADMIN']}>
                                <AppShell>
                                  <DeveloperConsole />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/developer/terms" element={
                              <ProtectedRoute>
                                <AppShell>
                                  <DeveloperTermsPage />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/developer/templates" element={
                              <ProtectedRoute requiredRoles={['DEVELOPER', 'ADMIN']}>
                                <AppShell>
                                  <NotificationTemplates />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            <Route path="/developer/templates/:templateId" element={
                              <ProtectedRoute requiredRoles={['DEVELOPER', 'ADMIN']}>
                                <AppShell>
                                  <TemplateEditor />
                                </AppShell>
                              </ProtectedRoute>
                            } />
                            */}
                            <Route path="/brand-theme-demo" element={<BrandThemeDemo />} />
                            <Route path="/brand-test" element={<BrandTestPage />} />
                            <Route path="/invoice-breakdown-demo" element={<InvoiceBreakdownDemo />} />
                            {/* Admin Routes - Protected and Lazy Loaded */}
                            <Route path="/admin" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminDashboard />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/analytics" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminAnalytics />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/users" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminUsers />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/transactions" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminTransactions />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/referrals" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminReferrals />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/support" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminSupport />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/guest-conversations" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminGuestConversations />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/votes" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <UserVotes />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/merchant-onboarding" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminMerchantOnboarding />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/merchant-insights" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminMerchantInsights />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/agents" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AIAgentManagement />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/clients" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminClients />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/settings" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <AdminSettings />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            <Route path="/admin/system" element={
                              <ProtectedAdminRoute>
                                <AppShell>
                                  <Suspense fallback={<AdminLoadingFallback />}>
                                    <SystemAdmin />
                                  </Suspense>
                                </AppShell>
                              </ProtectedAdminRoute>
                            } />
                            
                            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                            </EmbedRouteGuard>
                        </main>
                      </div>
                    </BrandThemeProvider>
                      </VaiuRedeemUxProvider>
                      </CashTokenCardProvider>
                  </ConfigProvider>
                </CookieConsentProvider>
              </BiometricProvider>
            </NotificationProvider>
            </BrowserRouter>
          </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
